import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AnimatedValue from "@/components/AnimatedValue";
import {
  Users, TrendingUp, CalendarClock, Layers, ArrowUpRight, Sparkles, Cpu, Shuffle, Zap, Check, X as XIcon,
  Flame, MessageSquare, EyeOff,
} from "lucide-react";

function currency(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
}

const StatCard = ({ label, value, sub, icon: Icon, testId, accent }) => (
  <div
    className="brutalist-card p-6 flex flex-col justify-between min-h-[140px]"
    data-testid={testId}
  >
    <div className="flex items-start justify-between">
      <span className="section-label">{label}</span>
      <div className={`w-8 h-8 flex items-center justify-center peak-glow-accent ${accent || "bg-[var(--peak-ink)] text-white"}`}>
        <Icon className="w-4 h-4" strokeWidth={2.5} />
      </div>
    </div>
    <div>
      <div className="font-heading font-black text-4xl tracking-tighter">
        <AnimatedValue value={value} />
      </div>
      {sub && <div className="section-label mt-1">{sub}</div>}
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [triageFeed, setTriageFeed] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [priority, setPriority] = useState([]);
  const [priorityBusy, setPriorityBusy] = useState(null); // contact_id currently acting
  const [overrideOpen, setOverrideOpen] = useState(null); // contact_id being overridden
  const [overrideBusy, setOverrideBusy] = useState(false);

  const loadPriority = () => {
    api.get("/dashboard/priority-inbox").then(({ data }) => setPriority(data || [])).catch(() => {});
  };
  const loadFeed = () => {
    api.get("/dashboard/triage-feed").then(({ data }) => setTriageFeed(data)).catch(() => {});
    api.get("/dashboard/triage-feed/candidates").then(({ data }) => setCandidates(data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    loadFeed();
    loadPriority();
    const onTriageUpdate = () => loadFeed();
    window.addEventListener("peak:triage-updated", onTriageUpdate);
    return () => window.removeEventListener("peak:triage-updated", onTriageUpdate);
  }, []);

  const openThread = (row) => {
    if (row.thread_key) {
      navigate(`/inbox?thread=${encodeURIComponent(row.thread_key)}`);
    } else {
      navigate(`/contacts/${row.contact_id}`);
    }
  };
  const aiReply = (row) => {
    if (!row.thread_key) {
      toast.error("No open thread with this contact yet");
      return;
    }
    navigate(`/inbox?thread=${encodeURIComponent(row.thread_key)}&ai=1`);
  };
  const dismissPriority = async (row) => {
    setPriorityBusy(row.contact_id);
    try {
      await api.post(`/dashboard/priority-inbox/${row.contact_id}/dismiss`);
      toast.success("Dismissed until next reply");
      setPriority((p) => p.filter((r) => r.contact_id !== row.contact_id));
    } catch (e) { toast.error(e.response?.data?.detail || "Dismiss failed"); }
    finally { setPriorityBusy(null); }
  };

  const doOverride = async (contactId, workflowId) => {
    setOverrideBusy(true);
    try {
      const { data } = await api.post(`/contacts/${contactId}/override-sequence`, { workflow_id: workflowId });
      toast.success(`Enrolled in ${data.workflow_name}`);
      setOverrideOpen(null);
      loadFeed();
    } catch (e) { toast.error(e.response?.data?.detail || "Override failed"); }
    finally { setOverrideBusy(false); }
  };
  const doApprove = async (row) => {
    setOverrideBusy(true);
    try {
      const { data } = await api.post(`/contacts/${row.contact_id}/pending-triage/approve`, { activity_id: row.activity_id });
      toast.success(`Sent · enrolled in ${data.workflow_name}`);
      loadFeed();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setOverrideBusy(false); }
  };
  const doSkip = async (row) => {
    setOverrideBusy(true);
    try {
      await api.post(`/contacts/${row.contact_id}/pending-triage/skip`, { activity_id: row.activity_id });
      toast.success("Skipped");
      loadFeed();
    } catch (e) { toast.error(e.response?.data?.detail || "Skip failed"); }
    finally { setOverrideBusy(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="section-label">Command centre</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase mt-1">
            Hey, {user?.name || "there"}.
          </h1>
          <p className="text-[var(--peak-muted)] mt-1 text-sm md:text-base">Here is what your revenue engine looks like right now.</p>
        </div>
        <Link
          to="/contacts"
          data-testid="dashboard-add-contact-cta"
          className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase tracking-widest px-6 py-3 inline-flex items-center gap-2 transition-colors"
        >
          Add contact <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Contacts" value={stats?.contacts_total ?? "—"} sub={`${stats?.leads ?? 0} leads · ${stats?.customers ?? 0} customers`} icon={Users} testId="stat-contacts" />
        <StatCard label="Open pipeline" value={currency(stats?.pipeline_value)} sub={`${stats?.open_deals ?? 0} active deals`} icon={TrendingUp} testId="stat-pipeline" accent="bg-[var(--peak-accent)] text-white" />
        <StatCard label="Booked today" value={stats?.appointments_today ?? "—"} sub="Appointments scheduled" icon={CalendarClock} testId="stat-appointments" />
        <StatCard label="Funnels live" value={`${stats?.funnels_published ?? 0}/${stats?.funnels_total ?? 0}`} sub="Published pages" icon={Layers} testId="stat-funnels" accent="bg-[var(--peak-blue-deep)] text-white" />
      </div>

      {priority.length > 0 && (
        <div className="brutalist-card p-6 mt-6" data-testid="priority-inbox-card">
          <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
            <div>
              <div className="section-label inline-flex items-center gap-1">
                <Flame className="w-3 h-3 text-[var(--peak-accent)]" /> Priority inbox · hottest leads right now
              </div>
              <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Reply to these first</h2>
            </div>
            <Link to="/inbox" data-testid="priority-open-inbox" className="text-xs font-bold uppercase tracking-widest underline hover:text-[var(--peak-accent)]">Open inbox</Link>
          </div>
          <ul className="divide-y divide-[var(--peak-border)]">
            {priority.map((row) => {
              const busy = priorityBusy === row.contact_id;
              const conf = row.score;
              const confColor = conf == null ? "" : conf >= 85 ? "bg-[#008A27] text-white" : conf >= 70 ? "bg-[#F0AD00] text-black" : "bg-[var(--peak-muted)] text-white";
              return (
                <li key={row.contact_id} className="py-3" data-testid={`priority-row-${row.contact_id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {row.has_unread_reply && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 bg-[var(--peak-accent)] text-white inline-flex items-center gap-1" data-testid="unread-pill">
                            <MessageSquare className="w-2.5 h-2.5" /> UNREAD REPLY
                          </span>
                        )}
                        {conf != null && (
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 ${confColor}`} data-testid="priority-score-pill" title="AI lead score">{conf}</span>
                        )}
                        <Link to={`/contacts/${row.contact_id}`} className="font-bold uppercase text-xs tracking-widest hover:text-[var(--peak-accent)] truncate">
                          {row.contact_name}
                        </Link>
                        {row.contact_company && <span className="text-xs text-[var(--peak-muted)] truncate">· {row.contact_company}</span>}
                      </div>
                      {row.latest_subject && (
                        <div className="text-sm mt-1 font-semibold truncate">{row.latest_subject}</div>
                      )}
                      {row.latest_preview && (
                        <div className="text-xs text-[var(--peak-muted)] truncate italic mt-0.5">“{row.latest_preview}”</div>
                      )}
                      {!row.latest_subject && row.score_reasoning && (
                        <div className="text-xs text-[var(--peak-muted)] truncate italic mt-1">{row.score_reasoning}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      {row.latest_date && (
                        <span className="text-[10px] font-mono text-[var(--peak-muted)]">{new Date(row.latest_date).toLocaleString()}</span>
                      )}
                      {row.thread_key && (
                        <button
                          onClick={() => aiReply(row)}
                          disabled={busy}
                          data-testid={`ai-reply-${row.contact_id}`}
                          className="bg-[var(--peak-blue-deep)] hover:bg-black text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1 disabled:opacity-60"
                        >
                          <Sparkles className="w-3 h-3" /> AI reply
                        </button>
                      )}
                      <button
                        onClick={() => openThread(row)}
                        disabled={busy}
                        data-testid={`open-thread-${row.contact_id}`}
                        className="border border-[var(--peak-ink)] hover:bg-black hover:text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1 disabled:opacity-60"
                      >
                        {row.thread_key ? "Open thread" : "View contact"}
                      </button>
                      <button
                        onClick={() => dismissPriority(row)}
                        disabled={busy}
                        data-testid={`dismiss-priority-${row.contact_id}`}
                        className="border border-[var(--peak-ink)] hover:bg-[#E50000] hover:text-white hover:border-[#E50000] text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1 disabled:opacity-60"
                        title="Hide until a new reply arrives"
                      >
                        <EyeOff className="w-3 h-3" /> Dismiss
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {triageFeed.length > 0 && (
        <div className="brutalist-card p-6 mt-6" data-testid="triage-feed-card">
          <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
            <div>
              <div className="section-label inline-flex items-center gap-1"><Cpu className="w-3 h-3 text-[#008A27]" /> Auto-triage feed · what Peak did on its own</div>
              <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Recently routed</h2>
            </div>
            <Link to="/automations" className="text-xs font-bold uppercase tracking-widest underline hover:text-[var(--peak-accent)]">Manage sequences</Link>
          </div>
          <ul className="divide-y divide-[var(--peak-border)]">
            {triageFeed.map((row) => {
              const isMatch = row.decision === "matched";
              const isGen = row.decision === "generated";
              const isOverride = row.decision === "override";
              const pill = isMatch ? { bg: "bg-[var(--peak-blue-deep)]", label: "MATCHED" }
                : isGen ? { bg: "bg-[var(--peak-accent)]", label: "GENERATED" }
                : isOverride ? { bg: "bg-[var(--peak-ink)]", label: "OVERRIDE" }
                : { bg: "bg-[var(--peak-muted)]", label: (row.decision || "?").toUpperCase() };
              const isPending = row.status === "pending";
              const isSkipped = row.status === "skipped";
              const conf = row.confidence;
              const confColor = conf == null ? "" : conf >= 85 ? "bg-[#008A27] text-white" : conf >= 55 ? "bg-[#F0AD00] text-black" : "bg-[#E50000] text-white";
              return (
                <li key={row.activity_id} className="py-3" data-testid={`triage-row-${row.activity_id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 text-white ${pill.bg}`}>{pill.label}</span>
                        {isPending && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-[#F0AD00] text-black" data-testid="pending-pill">PENDING</span>}
                        {isSkipped && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-[var(--peak-muted)] text-white">SKIPPED</span>}
                        {conf != null && <span className={`text-[10px] font-mono px-1.5 py-0.5 ${confColor}`} data-testid="confidence-pill" title="Claude's self-reported confidence">{conf}%</span>}
                        <Link to={`/contacts/${row.contact_id}`} className="font-bold uppercase text-xs tracking-widest hover:text-[var(--peak-accent)] truncate">
                          {row.contact_name}
                        </Link>
                        {row.contact_company && <span className="text-xs text-[var(--peak-muted)] truncate">· {row.contact_company}</span>}
                      </div>
                      <div className="text-sm mt-1 flex items-center gap-2 flex-wrap">
                        <Zap className="w-3 h-3 text-[var(--peak-muted)]" />
                        <span className="font-semibold truncate">{row.workflow_name}</span>
                      </div>
                      {row.reasoning && <div className="text-xs text-[var(--peak-muted)] mt-0.5 italic truncate">“{row.reasoning}”</div>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      <span className="text-[10px] font-mono text-[var(--peak-muted)]">{row.created_at ? new Date(row.created_at).toLocaleString() : ""}</span>
                      {isPending && (
                        <>
                          <button onClick={() => doApprove(row)} disabled={overrideBusy} data-testid={`approve-btn-${row.activity_id}`} className="bg-[#008A27] hover:bg-black text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1 disabled:opacity-60">
                            <Check className="w-3 h-3" /> Send now
                          </button>
                          <button onClick={() => doSkip(row)} disabled={overrideBusy} data-testid={`skip-btn-${row.activity_id}`} className="border border-[var(--peak-ink)] hover:bg-black hover:text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1 disabled:opacity-60">
                            <XIcon className="w-3 h-3" /> Skip
                          </button>
                        </>
                      )}
                      {candidates.length > 0 && !isSkipped && (
                        <button
                          onClick={() => setOverrideOpen(overrideOpen === row.contact_id ? null : row.contact_id)}
                          data-testid={`override-btn-${row.activity_id}`}
                          className="border border-[var(--peak-ink)] hover:bg-black hover:text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1"
                        >
                          <Shuffle className="w-3 h-3" /> Override
                        </button>
                      )}
                    </div>
                  </div>
                  {overrideOpen === row.contact_id && (
                    <div className="mt-2 p-3 bg-[var(--peak-surface-alt)] border border-[var(--peak-ink)]" data-testid={`override-panel-${row.activity_id}`}>
                      <div className="section-label mb-2">Route this contact into…</div>
                      <div className="flex flex-wrap gap-2">
                        {candidates.filter((c) => c.id !== row.workflow_id).map((c) => (
                          <button
                            key={c.id}
                            onClick={() => doOverride(row.contact_id, c.id)}
                            disabled={overrideBusy}
                            data-testid={`override-choice-${c.id}`}
                            className="bg-white border-2 border-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 disabled:opacity-60"
                          >
                            {c.name}
                          </button>
                        ))}
                        {candidates.filter((c) => c.id !== row.workflow_id).length === 0 && (
                          <div className="text-xs text-[var(--peak-muted)]">No other eligible sequences. Create one in Automations.</div>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 brutalist-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="section-label">Recent contacts</div>
              <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Latest to hit your CRM</h2>
            </div>
            <Link to="/contacts" data-testid="dashboard-view-all-contacts" className="text-xs font-bold uppercase tracking-widest underline hover:text-[var(--peak-accent)]">View all</Link>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-[var(--peak-ink)]">
                <th className="text-left text-xs font-bold uppercase tracking-wider text-[var(--peak-muted)] pb-3">Name</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-[var(--peak-muted)] pb-3">Email</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-[var(--peak-muted)] pb-3">Status</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-[var(--peak-muted)] pb-3">Source</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.recent_contacts || []).length === 0 && (
                <tr><td colSpan={4} className="py-8 text-center text-sm text-[var(--peak-muted)]">No contacts yet. Add your first one.</td></tr>
              )}
              {(stats?.recent_contacts || []).map((c) => (
                <tr key={c.id} className="border-b border-[var(--peak-border)] hover:bg-[var(--peak-surface-alt)]">
                  <td className="py-3 text-sm font-semibold">
                    <Link className="hover:text-[var(--peak-accent)]" to={`/contacts/${c.id}`}>
                      {(c.first_name || "") + " " + (c.last_name || "") || "—"}
                    </Link>
                  </td>
                  <td className="py-3 text-sm">{c.email || "—"}</td>
                  <td className="py-3 text-sm"><span className="px-2 py-1 text-xs font-bold uppercase tracking-wide bg-[var(--peak-surface-alt)] border border-[var(--peak-border)]">{c.status}</span></td>
                  <td className="py-3 text-sm text-[var(--peak-muted)]">{c.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="brutalist-card p-6 flex flex-col">
          <div className="section-label">AI copilot</div>
          <h3 className="font-heading font-bold text-2xl tracking-tight mt-1 mb-3">Ship faster.</h3>
          <p className="text-sm text-[var(--peak-muted)] mb-4">
            Ask the copilot to draft an outreach email, score a lead, or summarize a messy contact record. Powered by Claude Sonnet 4.5.
          </p>
          <div className="mt-auto grid grid-cols-1 gap-2">
            <div className="border border-[var(--peak-border)] p-3 text-sm">
              <div className="section-label mb-1">Example</div>
              "Draft an email to a lead who booked a demo but no-showed."
            </div>
            <div className="border border-[var(--peak-border)] p-3 text-sm">
              <div className="section-label mb-1">Example</div>
              "Score this lead based on activity below."
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[var(--peak-blue-deep)]">
            <Sparkles className="w-4 h-4" /> Open the AI copilot in the sidebar
          </div>
        </div>
      </div>
    </div>
  );
}
